import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog } from '@factorialco/f0-react'
import {
  Add as AddIcon,
  CheckCircle as TaskIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Pencil as PencilIcon,
  ThumbsUp as ApprovalIcon,
} from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { BuildingBlocksList } from 'modules/workflows/components/BuildingBlocksList'
import { NodeUIMultiAction } from 'modules/workflows/components/Node/types'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'
import useClickOutside from 'modules/workflows/hooks/useClickOutside'

import { SidepanelHeader } from './partials/SidepanelHeader'
import { SidepanelWrapper } from './partials/SidepanelWrapper'

const DROPDOWN_MIN_WIDTH = 160
const DROPDOWN_Z_INDEX = 10000

type Props = {
  node: NodeUIMultiAction
}

export const MultiActionNodeSidepanel = ({ node }: Props) => {
  const {
    setNodesUI,
    buildingBlocks,
    setMultiActionDraft,
    multiActionEditingTaskId,
    setMultiActionEditingTaskId,
  } = useWorkflowContext()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null
  )
  const [taskIdToDelete, setTaskIdToDelete] = useState<string | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const dropdownMenuRef = useRef<HTMLDivElement>(null)
  const clickOutsideRefs = useMemo(() => [buttonRef, dropdownMenuRef], [])

  const actionBlocks = useMemo(
    () => buildingBlocks.filter((block) => block.key !== 'condition'),
    [buildingBlocks]
  )

  useLayoutEffect(() => {
    if (!dropdownOpen || !buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.right - DROPDOWN_MIN_WIDTH,
    })
  }, [dropdownOpen])

  useClickOutside(clickOutsideRefs, () => setDropdownOpen(false))

  const handleClose = () => {
    setNodesUI((prev) => prev.map((n) => ({ ...n, selected: false })))
  }

  const handleNewActionClick = (nodeType: 'task' | 'approvals') => {
    setMultiActionDraft({
      nodeId: node.id,
      type: nodeType,
      taskId: crypto.randomUUID(),
    })
    setDropdownOpen(false)
  }

  const handleConfirmDeleteTask = () => {
    if (!taskIdToDelete) return
    const tasksAfterDelete = tasks.filter((t) => t.id !== taskIdToDelete)
    if (tasksAfterDelete.length < 2) return
    setNodesUI((prev) =>
      prev.map((n) => {
        if (n.id !== node.id || n.type !== 'multiAction') return n
        return {
          ...n,
          data: {
            ...n.data,
            parallelTasks: tasksAfterDelete,
          },
        }
      })
    )
    if (multiActionEditingTaskId === taskIdToDelete) setMultiActionEditingTaskId(null)
    setTaskIdToDelete(null)
  }

  const tasks = node.data.parallelTasks ?? []
  const canDeleteTask = tasks.length > 2

  return (
    <>
      {taskIdToDelete !== null && (
        <F0Dialog
          isOpen
          title={i18n.t('workflows.sidepanel.multi_action.delete_confirm_title')}
          description={i18n.t('workflows.sidepanel.multi_action.delete_confirm_description')}
          onClose={() => setTaskIdToDelete(null)}
          primaryAction={{
            label: i18n.t('common.delete'),
            onClick: handleConfirmDeleteTask,
            variant: 'critical',
          }}
          secondaryAction={{
            label: i18n.t('common.cancel'),
            onClick: () => setTaskIdToDelete(null),
          }}
          width="sm"
        />
      )}
      <SidepanelWrapper
        header={
          <SidepanelHeader
            nodeId={node.id}
            backgroundColor="#FDEBD0"
            icon={
              <GroupIcon width={22} height={22} style={{ color: '#E67E22', strokeWidth: 1.5 }} />
            }
            title={i18n.t('workflows.node_type.multi_action.edit')}
            onClose={handleClose}
            showDuplicateButton={false}
          />
        }
      >
        <Box flexDirection="column" gap="s12">
          <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" width="full">
            <Text color="textPrimary" size="200" weight="medium">
              {i18n.t('workflows.sidepanel.multi_action.actions')}
            </Text>
            <Box position="relative" ref={buttonRef}>
              <Box style={{ display: 'inline-block' }}>
                <F0Button
                  variant="outline"
                  size="md"
                  icon={AddIcon}
                  label={i18n.t('workflows.sidepanel.multi_action.new_action')}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    setDropdownOpen((prev) => !prev)
                  }}
                />
              </Box>
              {dropdownOpen &&
                dropdownPosition &&
                createPortal(
                  <Box
                    ref={dropdownMenuRef}
                    position="absolute"
                    background="white"
                    border={{ all: { color: 'grey400', style: 'solid', width: 's1' } }}
                    borderRadius={{ all: 'abs012' }}
                    paddingY="s4"
                    paddingX="s4"
                    flexDirection="column"
                    gap="s2"
                    style={{
                      position: 'fixed',
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      minWidth: `${DROPDOWN_MIN_WIDTH}px`,
                      zIndex: `${DROPDOWN_Z_INDEX}`,
                    }}
                  >
                    <BuildingBlocksList
                      blocks={actionBlocks}
                      onSelect={(nodeType) => {
                        if (nodeType === 'task' || nodeType === 'approvals') {
                          handleNewActionClick(nodeType)
                        }
                      }}
                      showTooltip={false}
                      variant="compact"
                    />
                  </Box>,
                  document.body
                )}
            </Box>
          </Box>

          <Box flexDirection="column" gap="s8">
            {tasks.length === 0 ? (
              <Text color="grey600" size="200">
                {i18n.t('workflows.sidepanel.multi_action.no_tasks')}
              </Text>
            ) : (
              tasks.map((task) => {
                const blockTitle =
                  task.type === 'approvals'
                    ? i18n.t('workflows.building_blocks.approval.title')
                    : i18n.t('workflows.building_blocks.task.title')
                const buttonLabel = task.title?.trim() || blockTitle
                const TaskOrApprovalIcon = task.type === 'approvals' ? ApprovalIcon : TaskIcon
                const block = actionBlocks.find((b) => b.nodeType === task.type)
                const iconBackground = block?.background ?? 'transparent'
                const iconColor = block?.color ?? 'var(--f1-text-secondary)'

                return (
                  <Box key={task.id} flexDirection="row" alignItems="center" gap="s8" width="full">
                    <Box
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="flexStart"
                      gap="s8"
                      padding="s8"
                      borderRadius={{ all: 'abs008' }}
                      border={{ all: { style: 'solid', color: 'grey400', width: 's1' } }}
                      style={{ flex: '1', minWidth: '0' }}
                      width="full"
                    >
                      <Box
                        borderRadius={{ all: 'abs006' }}
                        padding="s4"
                        style={{ backgroundColor: iconBackground, flexShrink: '0' }}
                      >
                        <TaskOrApprovalIcon
                          width="20"
                          height="20"
                          style={{ color: iconColor, strokeWidth: '1' }}
                        />
                      </Box>
                      <Text size="200" weight="medium" color="textPrimary">
                        {buttonLabel}
                      </Text>
                    </Box>
                    <F0Button
                      size="lg"
                      icon={PencilIcon}
                      hideLabel
                      variant="outline"
                      label={i18n.t('common.edit')}
                      onClick={() => setMultiActionEditingTaskId(task.id)}
                    />
                    <F0Button
                      size="lg"
                      icon={DeleteIcon}
                      hideLabel
                      variant="critical"
                      label={i18n.t('common.delete')}
                      title={
                        !canDeleteTask
                          ? i18n.t(
                              'workflows.sidepanel.multi_action.min_tasks_delete_disabled_tooltip'
                            )
                          : undefined
                      }
                      disabled={!canDeleteTask}
                      onClick={() => canDeleteTask && setTaskIdToDelete(task.id)}
                    />
                  </Box>
                )
              })
            )}
          </Box>
        </Box>
      </SidepanelWrapper>
    </>
  )
}
